<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0

@component
File upload section for the transcription page.
Manages Uppy-based drag-and-drop uploads (TUS protocol), glossary file uploads,
and audio recordings. Fetches allowed file types, size limits and endpoints from
the backend configuration on mount.
-->
<script>
import Uppy from '@uppy/core';
import DragDrop from '@uppy/drag-drop';
import German from '@uppy/locales/lib/de_DE';
import English from '@uppy/locales/lib/en_US';
import StatusBar from '@uppy/status-bar';
import Tus from '@uppy/tus';
import { onDestroy, onMount } from 'svelte';
import { getApiUrl } from '../../config';
import { i18n } from '../../i18n.js';
import { TranscriptionJob } from '../../libs/TranscriptionAPI/TranscriptionJobs.js';
import { TranscriptionJobsStore } from '../../libs/TranscriptionAPI/TranscriptionJobsStore.js';
import {
  createTranscriptionMediaFileMeta,
  setTranscriptionMediaFile,
} from '../../libs/TranscriptionAPI/TranscriptionMediaFileStore.js';
import { get, post, upload } from '../../utils/CoreService';
import { HttpError, ValidationError } from '../../utils/Errors.js';
import { getToken, updateToken } from '../../utils/KeycloakService.js';
import { getMicroserviceConfig } from '../../utils/MicroserviceConfigs';
import { snackbarStore } from '../../utils/SnackbarStore';
import uiStore from '../../utils/UIStore';
import { getTranslatedUnit, minutesToValueAndUnit } from '../../utils/minutesToValueAndUnit';
import AudioRecorder from './AudioRecorder.svelte';

import '@uppy/core/dist/style.min.css';
import '@uppy/drag-drop/dist/style.min.css';
import '@uppy/status-bar/dist/style.min.css';

// Fachbegriffe upload
/** DOM element bound to the Uppy drag-and-drop zone. */
let glossaryFileInput;
/** DOM element bound to the Uppy drag-and-drop target. */
let uppyDragDropTarget;
/** DOM element bound to the Uppy status bar widget. */
let uppyStatusBarTarget;
/** Uppy instance; null until initUppy() is called on mount. */
let uppy = null;
/** Reactive list of upload row state objects shown in the UI. */
let uploadRows = [];
/** fileId → AbortController for in-flight upload-handoff polls. */
let activeHandoffPolls = new Map();
/** Cleanup function returned by the uiStore text subscription. */
let unsubscribeTextStore = () => {};

/** TUS resumable upload chunk size: 4 MiB. */
const TUS_UPLOAD_CHUNK_SIZE_BYTES = 4 * 1024 * 1024;
/** Uppy locale map keyed by language code. */
const uppyLocales = {
  de: German,
  en: English,
};

/** Human-readable German labels for each upload status value. */
const uploadStatusLabels = {
  queued: 'Wartet',
  creating: 'Upload wird vorbereitet',
  uploading: 'Upload läuft',
  processing: 'Upload abgeschlossen, Transkription wird vorbereitet',
  error: 'Fehler',
  canceled: 'Abgebrochen',
};

// Upload config loaded from the backend on mount.
/** Allowed audio file extensions (from backend config). */
let fileExtensionsAudio = [];
/** Allowed video file extensions (from backend config). */
let fileExtensionsVideo = [];
/** Merged and normalised list of all allowed upload extensions. */
let allowedUploadExtensions = [];
/** Maximum allowed upload size in bytes (from backend config). */
let maxUploadSizeBytes = 0;
/** TUS upload endpoint URL (from backend config). */
let tusUploadEndpoint = '';
/** Required CSV column names for glossary uploads (from backend config). */
let specialTermsRequiredColumns = [];
/** Allowed file extensions for glossary uploads (from backend config). */
let specialTermsUploadExtensions = [];
let specialTermsEnabled = false;
/** True once all backend upload config values have been fetched. */
let uploadConfigLoaded = false;

onMount(async () => {
  const [
    audioResponse,
    videoResponse,
    uploadBytesResponse,
    tusEndpointResponse,
    specialTermsRequiredColumnsResponse,
    specialTermsUploadExtensionsResponse,
    specialTermsEnabledResponse,
  ] = await Promise.all([
    getMicroserviceConfig('transcription', 'allowed_audio_files'),
    getMicroserviceConfig('transcription', 'allowed_video_files'),
    getMicroserviceConfig('transcription', 'max_upload_size_bytes'),
    getMicroserviceConfig('transcription', 'tus_upload_endpoint'),
    getMicroserviceConfig('transcription', 'special_terms_required_columns'),
    getMicroserviceConfig('transcription', 'special_terms_upload_extensions'),
    getMicroserviceConfig('transcription', 'special_terms_enabled'),
  ]);

  if (audioResponse !== undefined) fileExtensionsAudio = audioResponse;
  if (videoResponse !== undefined) fileExtensionsVideo = videoResponse;
  allowedUploadExtensions = normalizeExtensions([...fileExtensionsAudio, ...fileExtensionsVideo]);
  if (uploadBytesResponse !== undefined) maxUploadSizeBytes = uploadBytesResponse;
  if (tusEndpointResponse !== undefined) tusUploadEndpoint = tusEndpointResponse;
  if (specialTermsRequiredColumnsResponse !== undefined)
    specialTermsRequiredColumns = specialTermsRequiredColumnsResponse;
  specialTermsUploadExtensions = normalizeExtensions(
    specialTermsUploadExtensionsResponse ?? specialTermsUploadExtensions,
  );
  specialTermsEnabled = specialTermsEnabledResponse === true;
  uploadConfigLoaded = true;
});

// Keep these as extensions, because MIME types from browsers can be missing or inconsistent.
// Backend-provided format lists may contain values with or without a leading dot.
$: activeUploadCount = uploadRows.filter((row) => ['creating', 'uploading', 'processing'].includes(row.status)).length;
$: uploadSummary = activeUploadCount > 0 ? `${activeUploadCount} Datei(en) werden verarbeitet` : '';

/**
 * Normalise an array of file format strings into lowercase dot-prefixed extensions.
 * Deduplicates and removes empty entries.
 * @param {string[]} formats
 * @returns {string[]}
 */
function normalizeExtensions(formats) {
  if (!Array.isArray(formats)) return [];
  return [
    ...new Set(
      formats
        .map((format) =>
          String(format || '')
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean)
        .map((format) => (format.startsWith('.') ? format : `.${format}`)),
    ),
  ];
}

/**
 * Build a human-readable CSV format hint for the glossary upload dialog,
 * listing the required column names.
 * @returns {string}
 */
function glossaryFormatHint() {
  const columns = specialTermsRequiredColumns.join(', ');
  return (
    'Erwartetes CSV-Format: Spalten ' +
    columns +
    ' (Reihenfolge egal).\n' +
    'Zusätzlich müssen alle Felder in jeder Datenzeile vollständig ausgefüllt sein.'
  );
}

$: specialTermsFileAccept = specialTermsUploadExtensions.join(',');

/**
 * Extract the lowercase file extension (including the dot) from a file name.
 * Returns an empty string if no extension is found.
 * @param {string} fileName
 * @returns {string}
 */
function getFileExtension(fileName) {
  const normalizedName = String(fileName || '').toLowerCase();
  const dotIndex = normalizedName.lastIndexOf('.');
  return dotIndex >= 0 ? normalizedName.slice(dotIndex) : '';
}

/**
 * Return true when a file's extension and size are within the configured limits.
 * @param {File} file
 * @returns {boolean}
 */
function isAllowedUploadFile(file) {
  const extension = getFileExtension(file.name);
  const hasAllowedExtension = allowedUploadExtensions.includes(extension);

  return hasAllowedExtension && file.size <= maxUploadSizeBytes;
}

/**
 * Create a new upload row object from an Uppy file descriptor.
 * @param {{ id: string, name: string }} file  Uppy file object.
 * @returns {object}
 */
function createUploadRow(file) {
  return {
    id: file.id,
    uppyFileId: file.id,
    fileName: file.name,
    status: 'queued',
    progress: 0,
    jobId: null,
    errorMessage: '',
    isPaused: false,
  };
}

/**
 * Toggle pause/resume for an active Uppy upload and reflect the new state in the row.
 * @param {string} rowId  Uppy file ID.
 */
function pauseResumeUpload(rowId) {
  const file = uppy?.getFile(rowId);
  if (!file) return;

  uppy.pauseResume(rowId);

  const updatedFile = uppy.getFile(rowId);
  updateUploadRow(rowId, {
    isPaused: Boolean(updatedFile?.isPaused),
  });
}

/**
 * Cancel an upload that has not yet errored or been cancelled, and mark it as cancelled.
 * @param {string} rowId  Uppy file ID.
 */
function cancelUpload(rowId) {
  const row = getUploadRow(rowId);
  if (!row || ['error', 'canceled'].includes(row.status)) return;

  uppy?.removeFile(rowId);

  updateUploadRow(rowId, {
    status: 'canceled',
    progress: 0,
    isPaused: false,
  });
}

/**
 * Find and return the upload row with the given ID, or undefined if not found.
 * @param {string} rowId
 * @returns {object|undefined}
 */
function getUploadRow(rowId) {
  return uploadRows.find((row) => row.id === rowId);
}

/**
 * Merge changes into an existing upload row. No-op if the row does not exist.
 * @param {string} rowId
 * @param {object} changes  Partial row fields to merge.
 */
function updateUploadRow(rowId, changes) {
  const exists = uploadRows.some((row) => row.id === rowId);
  if (!exists) return;
  uploadRows = uploadRows.map((row) => (row.id === rowId ? { ...row, ...changes } : row));
}

/**
 * Insert a new upload row or merge changes into an existing one (matched by Uppy file ID).
 * @param {{ id: string, name: string }} uppyFile
 * @param {object} [changes={}]  Additional fields to set on the row.
 */
function addOrReplaceUploadRow(uppyFile, changes = {}) {
  const row = createUploadRow(uppyFile);
  const existing = getUploadRow(row.id);
  if (existing) {
    updateUploadRow(row.id, { ...row, ...changes });
  } else {
    uploadRows = [...uploadRows, { ...row, ...changes }];
  }
}

/**
 * Open the snackbar with a given severity and message.
 * @param {'success'|'error'|'confirmation'} severity
 * @param {string} message
 */
function showSnackbar(severity, message) {
  snackbarStore.update((current) => ({
    ...current,
    snackbarOpen: true,
    snackbarSeverity: severity,
    snackbarMessage: message,
  }));
}

/**
 * Return the Uppy locale object matching the given language code.
 * Falls back to German if the language is not supported.
 * @param {string} [language]
 * @returns {object}
 */
function getUppyLocale(language = i18n.language) {
  // get Uppy locale from UI locale; German as fallback
  return uppyLocales[String(language || '').split('-')[0]] || German;
}

/**
 * Build the localised "unsupported file" error message for a given file name.
 * @param {string} fileName
 * @returns {string}
 */
function unsupportedUploadFileMessage(fileName) {
  const template = uiTexts?.transcription?.unsupportedUploadFile || 'Nicht unterstützte Datei: {fileName}';
  return template.replace('{fileName}', fileName);
}

/**
 * Add a recorded audio file to Uppy for upload.
 * Throws if Uppy is not ready or if the file format/size is not allowed.
 * @param {File} file
 */
function addRecordingToUppy(file) {
  if (!uppy) {
    throw new Error(audioRecorderTexts.uploadNotReady);
  }

  if (!isAllowedUploadFile(file)) {
    const extension = getFileExtension(file.name) || file.type || 'unknown';
    const template =
      audioRecorderTexts.unsupportedFormat ||
      'The recording format {format} is not supported by the transcription service.';
    throw new Error(template.replace('{format}', extension));
  }

  uppy.addFile({
    name: file.name,
    type: file.type,
    data: file,
    source: 'AudioRecorder',
    meta: {
      filename: file.name,
      filetype: file.type,
      content_type: file.type,
      size: file.size,
    },
  });
}

/**
 * Handle the `recorded-audio` event from AudioRecorder.
 * Adds the file to Uppy and shows a success or error snackbar.
 * @param {CustomEvent<{file: File}>} event
 */
function handleRecordedAudio(event) {
  try {
    addRecordingToUppy(event.detail.file);
    showSnackbar('success', audioRecorderTexts.addedToUploadList);
  } catch (error) {
    showSnackbar('error', error?.message || audioRecorderTexts.addToUploadListError);
  }
}

/**
 * Handle the `recording-error` event from AudioRecorder.
 * Shows the error message in the snackbar.
 * @param {CustomEvent<{message: string}>} event
 */
function handleRecordingError(event) {
  showSnackbar('error', event.detail.message);
}

/**
 * Initialise (or reinitialise) the Uppy instance with all plugins and event handlers.
 * Attaches DragDrop, TUS, and StatusBar plugins and wires up upload lifecycle events.
 */
function initUppy() {
  uppy = new Uppy({
    autoProceed: false,
    allowMultipleUploadBatches: true,
    locale: getUppyLocale(),
    onBeforeFileAdded: (currentFile) => {
      if (!isAllowedUploadFile(currentFile)) {
        const message = unsupportedUploadFileMessage(currentFile.name);
        uppy?.info(message, 'error', 6000);
        showSnackbar('error', message);
        return false;
      }
      return true;
    },
  });

  uppy.use(DragDrop, {
    target: uppyDragDropTarget,
    note: '',
  });
  uppy.use(StatusBar, {
    target: uppyStatusBarTarget,
    hideUploadButton: false,
    hideAfterFinish: false,
    showProgressDetails: true,
    hidePauseResumeButton: true,
    hideCancelButton: true,
    doneButtonHandler: null,
  });

  uppy.addPreProcessor(async (fileIds) => {
    for (const fileId of fileIds) {
      const uppyFile = uppy.getFile(fileId);
      if (!uppyFile || uppyFile.meta?.job_id) continue;

      addOrReplaceUploadRow(uppyFile, {
        status: 'creating',
        progress: 0,
        errorMessage: '',
      });

      const session = await createUploadSession(uppyFile.data);

      uppy.setFileMeta(fileId, {
        ...uppyFile.meta,
        ...session.metadata,
        job_id: session.job_id,
      });
      updateUploadRow(fileId, {
        jobId: session.job_id,
        status: 'queued',
      });

      console.log(`Created upload session for file '${uppyFile.name}' with job_id='${session.job_id}'.`);
    }
  });

  uppy.use(Tus, {
    endpoint: tusUploadEndpoint,
    onBeforeRequest: async (request) => {
      // pass Keycloak auth token if required
      if (getToken()) {
        await updateToken(30);
      }

      const token = getToken();
      if (token) {
        request.setHeader('Authorization', 'Bearer ' + token);
      }
    },
    // Avoid one full-file PATCH: browser upload progress can reach 100%
    // before tusd has acknowledged the request, especially for large files.
    chunkSize: TUS_UPLOAD_CHUNK_SIZE_BYTES,
    retryDelays: [0, 1000, 3000, 5000],
    removeFingerprintOnSuccess: true,
    storeFingerprintForResuming: false,
    allowedMetaFields: ['job_id', 'filename', 'filetype', 'content_type', 'size'],
  });

  uppy.on('file-added', () => {
    // Uppy now owns the pre-upload file list.
    // Do not add rows to our transcription list until upload starts.
  });

  uppy.on('file-removed', (file) => {
    const row = getUploadRow(file.id);
    if (!row) return;

    // Cleanup after successful tus upload. Do not mark as canceled.
    if (['processing', 'error'].includes(row.status)) {
      return;
    }
    updateUploadRow(file.id, {
      status: 'canceled',
      progress: 0,
      isPaused: false,
    });
  });
  uppy.on('upload-started', (file) => {
    updateUploadRow(file.id, { status: 'uploading', errorMessage: '' });
  });

  uppy.on('upload-progress', (file, progress) => {
    const bytesTotal = progress.bytesTotal || file.size || 1;
    const percent = Math.min(100, Math.round((progress.bytesUploaded / bytesTotal) * 100));
    updateUploadRow(file.id, { status: 'uploading', progress: percent });
  });

  uppy.on('upload-pause', (fileId, isPaused) => {
    updateUploadRow(fileId, { isPaused });
  });

  uppy.on('upload-resume', (fileId) => {
    updateUploadRow(fileId, { isPaused: false });
  });

  uppy.on('upload-success', (file) => {
    const jobId = uppy.getFile(file.id)?.meta?.job_id;
    if (!jobId) {
      updateUploadRow(file.id, {
        status: 'error',
        errorMessage: 'Upload abgeschlossen, aber keine job_id gefunden.',
      });
      return;
    }

    updateUploadRow(file.id, {
      status: 'processing',
      progress: 100,
      jobId,
      isPaused: false,
    });

    const mediaFile = typeof File !== 'undefined' && file.data instanceof File ? file.data : null;
    startUploadHandoffPolling(file.id, jobId, file.name, mediaFile);

    // Remove only from Uppy's upload queue, not from our custom uploadRows list.
    setTimeout(() => {
      if (uppy?.getFile(file.id)) {
        uppy.removeFile(file.id);
      }
    }, 0);
  });

  uppy.on('upload-error', (file, error) => {
    if (!file) return;
    console.error('Upload failed for file: ', file.name, error);
    updateUploadRow(file.id, {
      status: 'error',
      errorMessage: error?.message || 'Upload fehlgeschlagen',
    });
  });

  uppy.on('cancel-all', () => {
    for (const row of uploadRows) {
      if (row.status !== 'error') {
        updateUploadRow(row.id, { status: 'canceled', progress: 0 });
      }
    }
  });
}

async function createUploadSession(file) {
  const response = await post(`${getApiUrl()}/transcription/uploads`, {
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
  });

  if (!response.ok) {
    throw new HttpError(`HTTP error ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  if (!data.job_id || !data.tus_endpoint || !data.metadata) {
    throw new ValidationError('Unexpected upload-session response');
  }
  return data;
}

/**
 * Throw an AbortError immediately if the given signal has already been aborted.
 * @param {AbortSignal|null} signal
 */
function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
}

/**
 * Return a Promise that resolves after `ms` milliseconds, or rejects with
 * an AbortError if the signal fires before the timeout.
 * @param {number} ms
 * @param {AbortSignal|null} signal
 * @returns {Promise<void>}
 */
function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Return true when the given error is a DOM AbortError.
 * @param {unknown} error
 * @returns {boolean}
 */
function isAbortError(error) {
  return error?.name === 'AbortError';
}

/**
 * Poll the upload-status endpoint until the backend confirms the job was created
 * or until the maximum number of attempts is reached.
 * @param {string} jobId
 * @param {AbortSignal|null} signal
 * @returns {Promise<object>}  The final status response object.
 */
async function waitForUploadHandoff(jobId, signal) {
  const maxAttempts = 120;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    throwIfAborted(signal);

    const response = await get(`${getApiUrl()}/transcription/upload_status/${encodeURIComponent(jobId)}`, { signal });

    if (!response.ok) {
      const detail = await extractErrorDetail(response);
      throw new HttpError(
        detail
          ? `Upload-Status konnte nicht abgefragt werden (${response.status}): ${detail}`
          : `Upload-Status konnte nicht abgefragt werden (${response.status}).`,
        response.status,
      );
    }

    const statusResponse = await response.json().catch(() => {
      throw new ValidationError('Unexpected upload-status response');
    });

    if (!statusResponse || typeof statusResponse.status !== 'string') {
      throw new ValidationError('Unexpected upload-status response');
    }

    if (statusResponse.status === 'error') {
      throw new Error(statusResponse.error_detail || 'Upload konnte nicht verarbeitet werden');
    }
    if (statusResponse.status === 'job_created') {
      return statusResponse;
    }

    await delay(1000, signal);
  }

  throw new Error('Upload wurde nicht rechtzeitig zur Transkription übergeben');
}

/**
 * Start background polling for a TUS upload handoff to the transcription backend.
 * On success, moves the file from the upload list to the TranscriptionJobsStore.
 * No-op if polling is already active for this file.
 * @param {string} fileId     Uppy file ID used as the upload row key.
 * @param {string} jobId      Backend job ID returned by the upload session.
 * @param {string} fileName   Display name shown in the jobs list.
 * @param {File|null} [mediaFile]  Original media file to keep in the media store.
 */
function startUploadHandoffPolling(fileId, jobId, fileName, mediaFile = null) {
  if (activeHandoffPolls.has(fileId)) return;

  const controller = new AbortController();
  const poll = waitForUploadHandoff(jobId, controller.signal)
    .then(() => {
      const job = new TranscriptionJob(jobId, fileName);
      if (mediaFile) {
        // Keep the original media only in this tab; localStorage gets metadata for later matching.
        setTranscriptionMediaFile(jobId, mediaFile);
        job.setMediaFileMeta(createTranscriptionMediaFileMeta(mediaFile));
      }
      TranscriptionJobsStore.addTranscriptionJob(job);
      // Remove from upload-phase list after it migrated to the transcription jobs list.
      uploadRows = uploadRows.filter((row) => row.id !== fileId);
      console.log(`Successfully completed upload session for job_id='${jobId}'.`);
    })
    .catch((error) => {
      if (isAbortError(error)) return;

      console.error('Upload handoff failed for file: ', fileName, error);
      updateUploadRow(fileId, {
        status: 'error',
        errorMessage: error?.message || 'Upload konnte nicht verarbeitet werden',
      });
    })
    .finally(() => {
      activeHandoffPolls.delete(fileId);
    });

  activeHandoffPolls.set(fileId, { controller, poll });
}

/**
 * Build the display label for an upload row, appending the progress percentage
 * when the upload is actively in progress.
 * @param {object} row  Upload row object.
 * @returns {string}
 */
function uploadStatusText(row) {
  const label = uploadStatusLabels[row.status] || row.status;
  return row.status === 'uploading' ? `${label}, ${row.progress}%` : label;
}

/**
 * Remove an upload row and its corresponding Uppy file entry.
 * @param {string} rowId
 */
function removeUpload(rowId) {
  uppy?.removeFile(rowId);
  uploadRows = uploadRows.filter((row) => row.id !== rowId);
}

/** Abort all active handoff polling loops and clear the tracking map. */
function abortActiveHandoffPolls() {
  for (const { controller } of activeHandoffPolls.values()) {
    controller.abort();
  }
  activeHandoffPolls.clear();
}

/**
 * Close and destroy the current Uppy instance.
 * Sets `uppy` to null before closing to prevent re-entrant calls.
 * @param {string} [reason='unmount']
 */
function closeUppy(reason = 'unmount') {
  if (!uppy || typeof uppy.close !== 'function') return;

  const currentUppy = uppy;
  uppy = null;
  currentUppy.close({ reason });
}

/**
 * Validates file header on Fachbegriffe, Bedeutung, Themenbereich
 */
async function handleGlossaryFile(event) {
  const file = event.target.files[0];
  if (!file) return;

  // Reset file input
  event.target.value = '';

  try {
    const text = await file.text();
    const lines = text.split('\n').filter((line) => line.trim());

    if (lines.length === 0) {
      snackbarStore.update((current) => ({
        ...current,
        snackbarOpen: true,
        snackbarSeverity: 'error',
        snackbarMessage: 'Die Datei ist leer.',
      }));
      return;
    }

    // Parse header row - handle BOM, quotes, different separators
    const header = lines[0]
      .replace(/^\uFEFF/, '') // Remove BOM
      .replace(/['"]/g, '') // Remove quotes
      .split(/[,;\t]/) // Split by comma, semicolon, or tab
      .map((col) => col.trim().toLowerCase());

    // Check for required columns (order doesn't matter)
    const requiredColumns = specialTermsRequiredColumns.map((column) => column.toLowerCase());
    const hasAllColumns = requiredColumns.every((col) => header.includes(col));

    if (!hasAllColumns) {
      snackbarStore.update((current) => ({
        ...current,
        snackbarOpen: true,
        snackbarSeverity: 'error',
        snackbarMessage: `Die Datei ist nicht im erwarteten CSV-Format.\n${glossaryFormatHint()}`,
      }));
      return;
    }

    const form = new FormData();
    form.append('file', file, file.name);

    try {
      const url = `${getApiUrl()}/transcription/special_terms/upload`;
      const response = await postGlossaryFile(url, form, {
        timeoutMs: 20000,
        retries: 1,
      });

      if (!response.ok) {
        const detail = await extractErrorDetail(response);
        const formattedDetail = formatGlossaryUploadErrorDetail(detail);
        let snackbar_message =
          response.status === 400
            ? `Ungültige Datei oder Spalten.\n${glossaryFormatHint()}`
            : response.status === 422
              ? `Upload fehlgeschlagen (${response.status}):\n${formattedDetail || 'Ungültige Eingabe.'}\n\n${glossaryFormatHint()}`
              : formattedDetail
                ? `Upload fehlgeschlagen (${response.status}):\n${formattedDetail}`
                : `Upload fehlgeschlagen (${response.status}).`;
        snackbarStore.update((current) => ({
          ...current,
          snackbarOpen: true,
          snackbarSeverity: 'error',
          snackbarMessage: snackbar_message,
        }));
        return;
      }

      const data = await response.json().catch(() => ({}));
      let snackbar_message = `Erfolg: Eingetragen ${data.inserted ?? 0}, aktualisiert ${data.updated ?? 0}, übersprungen ${data.skipped ?? 0}.`;
      snackbarStore.update((current) => ({
        ...current,
        snackbarOpen: true,
        snackbarSeverity: 'success',
        snackbarMessage: snackbar_message,
      }));
    } catch (e) {
      console.error('Error reading Fachbegriffe file:', e);
      snackbarStore.update((current) => ({
        ...current,
        snackbarOpen: true,
        snackbarSeverity: 'error',
        snackbarMessage: 'Zeitüberschreitung oder Netzwerkfehler beim Upload der Fachbegriffe.',
      }));
    }
  } catch (error) {
    console.error('Error reading Fachbegriffe file:', error);
    snackbarStore.update((current) => ({
      ...current,
      snackbarOpen: true,
      snackbarSeverity: 'error',
      snackbarMessage: 'Fehler beim Lesen der Datei.',
    }));
  }
}

/**
 * Extract a human-readable error detail from backend responses.
 */
async function extractErrorDetail(response) {
  try {
    const payload = await response.clone().json();
    if (payload && typeof payload.detail === 'string') {
      return payload.detail;
    }
    if (typeof payload === 'string') {
      return payload;
    }
  } catch (_e) {
    // Fallback to raw response text below.
  }

  const raw = await response.text().catch(() => '');
  if (!raw) {
    return '';
  }

  try {
    const payload = JSON.parse(raw);
    if (payload && typeof payload.detail === 'string') {
      return payload.detail;
    }
  } catch (_e) {
    // Keep raw text when body is not JSON.
  }

  return raw;
}

/**
 * Formats glossary upload backend details to one line per error block.
 */
function formatGlossaryUploadErrorDetail(detail) {
  if (!detail) {
    return '';
  }

  const normalized = detail.trim();
  const marker = 'Invalid input:';
  if (!normalized.toLowerCase().startsWith(marker.toLowerCase())) {
    return normalized;
  }

  const detailPart = normalized.slice(marker.length).trim();
  const errors = detailPart
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);

  if (errors.length === 0) {
    return normalized;
  }

  return [marker, ...errors].join('\n');
}

/**
 * Opens the file picker
 */
function openGlossaryPicker() {
  glossaryFileInput.click();
}

/**
 * POST helper to avoid timeout with a single retry
 */
async function postGlossaryFile(url, formData, { timeoutMs = 20000, retries = 1 } = {}) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await upload(url, formData, {
        signal: controller.signal,
      });
      clearTimeout(timer);
      return response;
    } catch (e) {
      clearTimeout(timer);
      if (attempt === retries) throw e;
      await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
    }
  }
}

/**
 * Update the Uppy locale when the app language changes.
 * Registered as a listener on the i18n `languageChanged` event.
 * @param {string} language  New language code (e.g. `'de'`, `'en'`).
 */
function updateUppyLanguage(language) {
  uppy?.setOptions({ locale: getUppyLocale(language) });
}

i18n.on('languageChanged', updateUppyLanguage);

onDestroy(() => {
  i18n.off('languageChanged', updateUppyLanguage);
  unsubscribeTextStore();
  abortActiveHandoffPolls();
  closeUppy();
});

//load ui texts from ui Store
let uiTexts;
/** Translated retention hint string built from backend config and i18n template. */
let translatedHintDeletion = '';
unsubscribeTextStore = uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
});

$: audioRecorderTexts = uiTexts?.transcription?.audioRecorder || {};

/**
 * Get the translated retention hint text with the time as configured in backend
 */
$: if (uiTexts) {
  loadTranslatedHintDeletion(uiTexts);
}

async function loadTranslatedHintDeletion(uiTexts) {
  const minutes = await getMicroserviceConfig('transcription', 'transcript_retention_period_minutes');
  if (minutes == null) return;

  const { value, unit } = minutesToValueAndUnit(minutes);
  const translatedUnitText = getTranslatedUnit(unit, value, uiTexts);
  const template = uiTexts?.transcription?.hintDeletion ?? '';

  translatedHintDeletion = template.replace('{value}', String(value)).replace('{unit}', translatedUnitText);
}

$: if (uploadConfigLoaded && uppyDragDropTarget && uppyStatusBarTarget && !uppy) {
  initUppy();
}
</script>

<svelte:window
  on:beforeunload={() => {
    abortActiveHandoffPolls();
    closeUppy('unmount');
  }}
/>

<div class="upload-section">
  <div class="upload-details">
    <p>{uiTexts.transcription.fileFormats}
      <span class="tooltip" style="display: inline-block;">
        <svg class="info-icon" aria-label="Unterstützte Datei-Formate" xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="#000000" viewBox="0 0 256 256"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"></path></svg>
        <span class="tooltiptext">
          <strong>{uiTexts.transcription.fileFormatsAudio}:</strong><br />
          {fileExtensionsAudio.join(', ')}<br /><br />
          <strong>{uiTexts.transcription.fileFormatsVideo}:</strong><br />
          {fileExtensionsVideo.join(', ')}
        </span>
      </span>
    </p>
    <p class="hint-text">
      {uiTexts.transcription.fileWeight}<br />
      {@html translatedHintDeletion }
    </p>
    {#if uploadSummary}
      <div class="upload-progress-status" role="status" aria-live="polite">
        <span class="upload-progress-label">{uploadSummary}</span>
      </div>
    {/if}
    {#if specialTermsEnabled}
      <details class="glossary-dropdown">
        <summary style="cursor: pointer;">{uiTexts.transcription.furtherOptions}</summary>
        <div>
          <p class="hint-text">
            {uiTexts.transcription.hintKeywords}
          </p>
          <button class="secondary upload" on:click={openGlossaryPicker}>
            {uiTexts.transcription.keywordsUpload}
          </button>
        </div>
      </details>
    {/if}
  </div>

  {#if specialTermsEnabled}
    <input bind:this={glossaryFileInput} type="file" accept={specialTermsFileAccept} on:change={handleGlossaryFile} hidden />
  {/if}
</div>

<div class="upload-methods">
  <div class="uppy-upload-wrapper">
    <div class="uppy-dragdrop-wrapper" bind:this={uppyDragDropTarget}></div>
    <div class="uppy-statusbar-wrapper" bind:this={uppyStatusBarTarget}></div>
  </div>

  <div class="upload-separator">{uiTexts.transcription.uploadSeparator}</div>

  <AudioRecorder texts={audioRecorderTexts} on:recorded={handleRecordedAudio} on:recordingerror={handleRecordingError} />
</div>

{#if uploadRows.length}
  <h3 class="uploaded-files-heading">
    {uiTexts.transcription.currentUploadsHeading}
  </h3>
  <div class="upload-list-wrapper">
    <ul class="upload-list">
      {#each uploadRows as uploadRow}
        <li class="upload-list-item">
          <div class="upload-file-main">
            <span class="upload-file-name">{uploadRow.fileName}</span>
            <span class="upload-file-status">{uploadStatusText(uploadRow)}</span>
            {#if uploadRow.errorMessage}
              <span class="upload-file-error">{uploadRow.errorMessage}</span>
            {/if}
          </div>
          {#if uploadRow.status === 'uploading' && uppy?.getFile(uploadRow.uppyFileId)}
            <button class="secondary upload-cancel" on:click={() => pauseResumeUpload(uploadRow.uppyFileId)}>
              {uploadRow.isPaused ? uiTexts.transcription.resumeUpload : uiTexts.transcription.pauseUpload}
            </button>

            <button class="secondary upload-cancel" on:click={() => cancelUpload(uploadRow.uppyFileId)}>
              {uiTexts.transcription.cancelUpload}
            </button>
          {/if}

          {#if ['error', 'canceled'].includes(uploadRow.status)}
            <button class="secondary upload-cancel" on:click={() => removeUpload(uploadRow.uppyFileId)}>
              {uiTexts.transcription.removeUpload}
            </button>
          {/if}
        </li>
      {/each}
    </ul>
  </div>
{/if}

<style>
  .upload-section{
		display:flex;
		gap:20px;
		align-items: center;
    justify-content: center;
		width:100%;
		background-color: #f2f2f2;
	}
	.upload-details{
		flex:4;
		display: flex;
		gap: 8px;
		flex-direction: column;
		text-align: left;
		padding: 12px 0;
	}
	.hint-text{
		font-size: 0.925rem; color: #666;
		margin-bottom: 8px;
	}
	button.secondary{
		width: auto;
		white-space:nowrap
	}
  button.secondary.upload{
		width: auto;
		padding-left: 40px;
		background-image: url(static/icon-upload.svg);
		background-repeat: no-repeat;
		background-position: left center;
		background-size: 40px;
	}
  .upload-methods {
    width: 100%;
    max-width: 1120px;
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: 28px;
    margin: 1.25rem auto;
  }
  .uppy-upload-wrapper {
    flex: 1;
    min-width: 280px;
    max-width: 520px;
    margin: 0;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }
  .upload-separator {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 1.15rem;
    font-weight: 600;
    text-transform: lowercase;
    padding: 0 10px;
  }
  .uppy-dragdrop-wrapper {
    width: 100%;
  }
  .uppy-statusbar-wrapper {
    width: 100%;
  }
  .uppy-dragdrop-wrapper :global(.uppy-DragDrop-container) {
    background-color: #f2f2f2;
    border-color: #c8c8c8;
    color: #333;
  }
  .uppy-dragdrop-wrapper :global(.uppy-DragDrop-container:hover),
  .uppy-dragdrop-wrapper :global(.uppy-DragDrop-container:focus),
  .uppy-dragdrop-wrapper :global(.uppy-DragDrop--isDraggingOver) {
    background-color: #e8e8e8;
    border-color: #999;
    color: #333;
  }
  .uppy-dragdrop-wrapper :global(.uppy-DragDrop-arrow) {
    fill: #9a9a9a;
  }
  .uppy-dragdrop-wrapper :global(.uppy-DragDrop-browse) {
    color: #333;
    text-decoration: underline;
  }
  @media screen and (max-width: 900px) {
    .upload-methods {
      flex-direction: column;
    }
    .uppy-upload-wrapper {
      width: 100%;
      margin: 0 auto;
    }
    .upload-separator {
      min-height: 24px;
    }
  }
  .uploaded-files-heading {
    width: 100%;
    max-width: 640px;
    margin: 1.5rem auto 1rem;
    padding-top: 1rem;
    border-top: 2px solid #ddd;
    font-size: 1rem;
    font-weight: 600;
    text-align: center;
  }
  .upload-list{
    background-color: #fff;
    border-radius: 0.0rem;
    list-style: none;
    padding: 0;
    width: 100%;
    margin-bottom:24px;
  }
  .upload-list-wrapper {
    width: 100%;
    max-width: 520px;
    margin: 1rem auto;
    max-height: none;
    overflow-y: auto;
  }
  .upload-list-item{
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    text-align: left;
  }
  .upload-file-main{
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .upload-file-name{
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    white-space: nowrap;
  }
  .upload-file-status{
    font-size: 0.9rem;
    color: #555;
  }
  .upload-file-error{
    font-size: 0.9rem;
    color: #b00020;
  }
  .upload-cancel{
    flex: 0 0 auto;
  }
  .upload-progress-status{
    width: 100%;
    max-width: 460px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .upload-progress-label{
    font-size: 0.95rem;
    color: #2e2e2e;
  }
</style>
